import { Component, EventEmitter, forwardRef, Input, OnChanges, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TfDropdownService } from "../tf-drop-down.service";
import { DropdownOption } from "../model/DropdownOption";
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {LocalStorageService} from '@app/services/local-storage-service';

@Component({
    selector: 'tf-dropdown',
    templateUrl: './tf-dropdown.component.html',
    styleUrls: ['./tf-dropdown.component.scss'],
    outputs: ['onChange', 'onClick', 'onReset'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => TfDropdownComponent),
        multi: true,
    }]
})

export class TfDropdownComponent implements OnChanges, ControlValueAccessor {

    @Input() options: DropdownOption[] | any[] = [];
    @Input() size: string = 'medium';
    @Input() inlineLabel: string = "Select";
    @Input() externalLabel: boolean = false;
    @Input() optionLabelKey: string = "label";
    @Input() optionValueKey?: string = undefined;
    @Input() search: boolean = false;
    @Input() searchPlaceHolder: string = "search";
    @Input() disable = false;
    @Input() virtualScroll: boolean = false;
    @Input() scrollHeight: number;
    @Input() overlayWidth: string = "auto";
    @Input() hasBlueBorder: boolean = false;
    @Input() compareFn: (a: unknown, b: unknown) => boolean = null;

    onChange: EventEmitter<any> = new EventEmitter();
    onClick: EventEmitter<any> = new EventEmitter();
    onReset: EventEmitter<any> = new EventEmitter();

    onSelect: boolean = false;
    documentClickListener: any;
    selectedOption: any = null;
    optionSelectKey: string = null;
    visibleVal: string;
    upArrow: boolean = false;
    isOptionSelected: boolean = false;
    selfClick: boolean = false;
    visibleOptions: any;
    searchInputValue: string;
    isSearchFilter: boolean = false;
    isArrayOfStrings: boolean = false;

    _onTouched: () => void = () => {
    };

    _onChange: (item: unknown) => void = () => {
    };

    constructor(
        public renderer: Renderer2,
        private dropdownService: TfDropdownService,
        public sanitizer: DomSanitizer,
        private localStorageService: LocalStorageService
    ) {
    }

    ngOnChanges(): void {

        this.isArrayOfStrings = this.dropdownService.isArrayOfStringType(this.options);

        if (this.selectedOption) {
            if ((!this.selectedOption[this.optionLabelKey] || (this.selectedOption[this.optionLabelKey] && this.selectedOption[this.optionLabelKey].trim() == "")) && !this.isArrayOfStrings) {
                this.resetDropdown();
            } else {
                this.preFill();
            }
        }
        this.updateLabel();

    }

    safeSvg(svg: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(svg);
    }

    show(): void {
        if (!this.onSelect) {
            this.onSelect = true;
        }
        this.filterOptions();
        this.upArrow = true;
        this.bindDocumentClickListener();
    }

    hide(): void {
        this.onSelect = false;
        this.upArrow = false;
        if (this.isSearchFilter) {
            this.searchInputValue = '';
            this.filterOptions();
        }
        this.unbindDocumentClickListener();
    }

    toggleDropDown(event): void {
        if (this.onSelect) {
            this.hide();
            this._onTouched();
        } else {
            this.show();
        }
        this.onClick.emit({
            originalEvent: event,
            selectedOption: this.selectedOption
        });
        this.selfClick = true;
    }

    resetDropdown(event = null): void {
        event && event.stopPropagation();
        this.isOptionSelected = false;
        this.visibleVal = this.inlineLabel;
        this.selectedOption = null;
        this.hide();
        this.onReset.emit(this.selectedOption);
        this._onChange(this.selectedOption);
    }

    getValue(event, option): void {
        event.stopPropagation();
        this.selectedOption = option;
        this.isOptionSelected = true;
        this.updateLabel();
        if (this.onSelect) {
            this.hide();
        } else {
            this.show();
        }
        this._onChange(this.selectedOption);
        this.onChange.emit({
            originalEvent: event,
            selectedOption: this.selectedOption
        });
    }

    preFill(): void {
        if (this.selectedOption && (this.selectedOption[this.optionLabelKey] || this.isArrayOfStrings || (this.optionSelectKey && this.selectedOption[this.optionSelectKey]))) {
            this.isOptionSelected = true;
        } else {
            this.selectedOption = {}
        }
        this.updateLabel();
    }

    updateLabel(): void {
        if (this.selectedOption && this.selectedOption[this.optionLabelKey]) {
            this.visibleVal = this.selectedOption[this.optionLabelKey];
        } else if (this.selectedOption && this.isArrayOfStrings) {
            this.visibleVal = this.selectedOption;
        } else {
            this.visibleVal = this.inlineLabel;
        }
    }

    filterOptions(): void {
        this.selfClick = true;
        if (this.searchInputValue && this.searchInputValue.length > 0) {
            this.isSearchFilter = true;
            if (this.isArrayOfStrings) {
                this.visibleOptions = this.dropdownService.filterBySearch(this.options, null, this.searchInputValue)
            } else {
                this.visibleOptions = this.dropdownService.filterBySearch(this.options, this.optionLabelKey, this.searchInputValue);
            }
        } else {
            this.isSearchFilter = false;
            this.visibleOptions = this.options;
        }
    }

    bindDocumentClickListener(): void {
        if (!this.documentClickListener) {
            this.documentClickListener = this.renderer.listen('document', 'click', () => {
                if (!this.selfClick && this.onSelect) {
                    this.hide();
                    this._onTouched();
                }
                this.selfClick = false;
            });
        }
    }

    unbindDocumentClickListener(): void {
        if (this.documentClickListener) {
            this.documentClickListener();
            this.documentClickListener = null;
        }
    }

    onValueChange($event) {
        console.log("$event", $event);
    }

    writeValue(value: any): void {

        if (!value) {
            this.selectedOption = null;
        } else if (!this.isValueSelected(value) && this.findIndexInList(this.options, value) > -1) {
            this.selectedOption = this.options[this.findIndexInList(this.options, value)];
        }

        if (this.selectedOption) {
            this.preFill();
        } else {
            this.isOptionSelected = false;
            this.visibleVal = this.inlineLabel;
            this.hide();
        }


    }

    registerOnChange(fn: (selectedOption: any) => void): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(disabled: boolean) {
        this.disable = disabled;
    }

    onSearchInputBlur() {
        this.selfClick = false;
    }

    selectByKey(value, key): void {

        if (this.isArrayOfStrings) {
            this.writeValue(value);
        } else {
            this.optionSelectKey = key;
            const convertedObject = {};
            convertedObject[this.optionSelectKey] = value;
            this.writeValue(convertedObject);
            this.optionSelectKey = null;
        }

        this._onChange(this.selectedOption);
        this.onChange.emit({
            originalEvent: null,
            selectedOption: this.selectedOption
        });

    }

    findIndexInList(optionList, optionItem): number {

        for (let i = 0; i < optionList.length; i++) {
            if (this._compare(optionList[i], optionItem)) {
                return i;
            }
        }

        return -1;

    }

    private _compare(a: DropdownOption, b: DropdownOption) {
        if (typeof this.compareFn === 'function') {
            return this.compareFn(a, b);
        } else {
            return a === b;
        }
    }

    isValueSelected(option) {
        return this.selectedOption && this.findIndexInList([this.selectedOption], option) != -1;
    }

    getActiveClass(item): string[] {
        const classes: string[] = []
        if (this.isArrayOfStrings) {
            if (this.selectedOption && item == this.selectedOption) {
                classes.push("active");
            }
        } else {
            if (this._compare(this.selectedOption, item)) {
                classes.push("active");
            }
        }

        return classes;
    }

    getDisabledClass(item): string[] {
        const classes: string[] = []

        if (item.disabled === true) {
            classes.push('tf-disabled-option');
        }

        const showMultiplexOptions = this.localStorageService.getItem('showGEMultiplexOptions') || false;

        if (this.localStorageService.isNPAssaysFeatureEnabled() && !showMultiplexOptions && item.availability === 'multiplexEligible') {
            classes.push('tf-disabled-option');
        }

        return classes;
    }

}
